/**
 * The scenario workspace's non-visual half: which mode the page is in, what
 * changed between the form and the version it was loaded from, and the toast a
 * re-price ends with.
 *
 * Shared by every sizer that opens a persisted scenario (RTL, GUC, DSCR), so
 * the three describe a change, a mode and a new version in the same words.
 */
import { useCallback } from "react";
import { useSearchParams } from "react-router-dom";

import { addressLabel } from "../../../_src_shared/utils/functions/addressLabel";
import { humanize } from "../sizerFormat";
import { fmtDrift, versionFigures } from "../scenarioModel";

/* ============================================================ Mode */

export const WORKSPACE_MODES = { REVIEW: "review", EDIT: "edit" };

/**
 * `?mode=review|edit`, in the URL so a link (and Back) lands on the same view.
 * A resumed scenario opens in Review unless asked otherwise — a list row opens
 * the answer, "Edit & re-price" opens the form. A blank sizer is always Edit:
 * there is nothing to review yet.
 */
export function useWorkspaceMode(hasScenario) {
  const [params, setParams] = useSearchParams();
  const raw = params.get("mode");
  const mode = !hasScenario
    ? WORKSPACE_MODES.EDIT
    : raw === WORKSPACE_MODES.EDIT
      ? WORKSPACE_MODES.EDIT
      : WORKSPACE_MODES.REVIEW;
  const setMode = useCallback(
    (next) =>
      setParams(
        (prev) => {
          const p = new URLSearchParams(prev);
          p.set("mode", next);
          return p;
        },
        { replace: true },
      ),
    [setParams],
  );
  return [mode, setMode];
}

/* ============================================================ Changes */

// Field labels for the "N changes since vN" list. Anything not here is
// humanized from its key, so a new field still reads as words.
const LABELS = {
  loanProductType: "Product",
  loanPurpose: "Purpose",
  termMonths: "Term",
  sizerInitialRequestedLeverage: "Initial leverage",
  financedBudgetPercent: "Financed budget",
  correspondentId: "Correspondent",
  delayedPurchase: "Delayed purchase",
  entityName: "Entity name",
  entityType: "Entity type",
  numberOfEntityMembers: "Members",
  numberOfGuarantors: "Guarantors",
  ficoMid: "FICO",
  resolvedCreditScore: "FICO",
  cashLiquidity: "Cash liquidity",
  otherLiquidity: "Other liquidity",
  citizenshipStatus: "Citizenship",
  entityPercentOwnership: "Ownership",
  asIsValue: "As-is value",
  purchasePrice: "Purchase price",
  purchaseDate: "Purchase date",
  constructionBudget: "Rehab budget",
  arv: "ARV",
  propertyType: "Property type",
  residentialSqFootage: "Sq ft",
  zip: "ZIP",
  zipCode: "ZIP",
  requestedLoanAmount: "Loan amount",
  requestedCoupon: "Requested coupon",
  totalRents: "Rents",
  totalMarketRents: "Market rents",
  annualTaxes: "Taxes",
  annualInsurance: "Insurance",
  // DSCR form keys
  annualFloodInsurance: "Flood insurance",
  annualHoaFees: "HOA fees",
  annualOtherExpense: "Other expense",
  vacancySelection: "Vacancy basis",
  recentRenovationOrConstruction: "Recent renovation",
  amortizationType: "Amortization",
  armProduct: "Rate structure",
  crossCollateralized: "Cross-collateralized",
  ruralExceptionRequested: "Rural exception",
  ruralExceptionNote: "Rural exception note",
  unitCount: "Units",
  prepaymentPenaltyStructure: "Prepay",
  firstName: "First name",
  lastName: "Last name",
};

// Repeaters read as "Applicant 1 · FICO" rather than "borrowers.0.ficoMid".
const GROUPS = {
  borrowers: "Applicant",
  subjectProperties: "Property",
  units: "Unit",
  entities: "Entity",
};

const MONEY_KEY =
  /amount|value|price|budget|^arv$|liquidity|rent|taxes|insurance|holdback|payment/i;
const FRACTION_KEY = /leverage|percent|ownership|coupon|rate/i;

const norm = (v) => {
  if (v === undefined || v === null || v === "") return null;
  if (typeof v === "string" && v.trim() !== "" && !Number.isNaN(Number(v))) {
    return Number(v);
  }
  return v;
};

/** Leaves of a form-values object, keyed by dotted path. Addresses stay whole. */
function flatten(obj, path = [], out = {}) {
  if (obj === null || obj === undefined || typeof obj !== "object") {
    out[path.join(".")] = norm(obj);
    return out;
  }
  if (path[path.length - 1] === "address" && !Array.isArray(obj)) {
    out[path.join(".")] = addressLabel(obj) || obj.zip || null;
    return out;
  }
  const entries = Array.isArray(obj)
    ? obj.map((v, i) => [i, v])
    : Object.entries(obj);
  if (!entries.length) out[path.join(".")] = null;
  entries.forEach(([k, v]) => flatten(v, [...path, String(k)], out));
  return out;
}

const labelFor = (path) => {
  const parts = path.split(".");
  const key = parts[parts.length - 1];
  const prefix = [];
  parts.slice(0, -1).forEach((p, i, arr) => {
    if (GROUPS[p] && /^\d+$/.test(arr[i + 1] || "")) {
      prefix.push(`${GROUPS[p]} ${Number(arr[i + 1]) + 1}`);
    } else if (p === "experience") {
      prefix.push("Experience");
    }
  });
  const own = LABELS[key] || humanize(key);
  return [...prefix, own].join(" · ");
};

const formatValue = (path, v) => {
  if (v === null || v === undefined) return "—";
  if (typeof v === "boolean") return v ? "Yes" : "No";
  const key = path.split(".").pop();
  if (typeof v === "number") {
    if (key === "termMonths") return `${v} mo`;
    if (FRACTION_KEY.test(key) && Math.abs(v) <= 1.5) {
      return `${+(v * 100).toFixed(3)}%`;
    }
    if (MONEY_KEY.test(key)) {
      return `$${Math.round(v).toLocaleString("en-US")}`;
    }
    return v.toLocaleString("en-US");
  }
  return String(v);
};

/**
 * What the form changed against `base` (the version it was loaded from, or
 * what last priced), as `[{ path, label, from, to }]`. Blank, null and
 * undefined are the same value; "90" and 90 are the same value — a field the
 * user cleared and re-typed is not a change.
 */
export function diffScenarioValues(base, current) {
  if (!base || !current) return [];
  const a = flatten(base);
  const b = flatten(current);
  const paths = Array.from(new Set([...Object.keys(a), ...Object.keys(b)]));
  return paths
    // Form-only keys (`_delayedPurchase`) mirror a real field already listed.
    .filter((p) => p && !p.startsWith("_") && (a[p] ?? null) !== (b[p] ?? null))
    .map((p) => ({
      path: p,
      label: labelFor(p),
      from: formatValue(p, a[p] ?? null),
      to: formatValue(p, b[p] ?? null),
    }));
}

/* ============================================================ Toast */

/**
 * The toast a re-price ends with: the new version, its headline figure, and
 * how far it moved from the version it was priced from.
 */
export function pricedToastText({
  scenarioType,
  data,
  prevResult,
  prevVersion,
}) {
  const now = versionFigures(scenarioType, data);
  const before = versionFigures(scenarioType, prevResult);
  const drift =
    now.primaryRaw != null && before.primaryRaw != null
      ? Math.round((now.primaryRaw - before.primaryRaw) * 10000)
      : null;
  return [
    data?.scenarioVersion ? `Priced v${data.scenarioVersion}` : "Priced",
    now.primary && now.primary !== "—" ? now.primary : null,
  ]
    .filter(Boolean)
    .join(" · ")
    .concat(
      drift != null && prevVersion
        ? ` (${fmtDrift(drift)} vs v${prevVersion})`
        : "",
    );
}
